import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, X, RefreshCw, Home, AlertTriangle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Define markdown components outside the component
const markdownComponents = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-purple-900 mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-stone-800 mt-4 mb-2 flex items-center gap-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-stone-700 mt-3 mb-1">{children}</h3>,
  p: ({ children }) => <p className="text-stone-600 mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-3 text-stone-600">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 mb-3 text-stone-600">{children}</ol>,
  li: ({ children }) => <li className="text-stone-600">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-stone-800">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-purple-300 pl-4 italic text-stone-500 my-3">
      {children}
    </blockquote>
  ),
};

const AllHivesAIReport = ({ open, onClose, token }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    setReport(null);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/hives/ai-all-hives-report`, { headers });
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate apiary report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchReport();
    }
  }, [open, fetchReport]);

  const handleClose = useCallback(() => {
    setReport(null);
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold text-stone-800 font-heading flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-purple-600" />
            AI Apiary Report
          </DialogTitle>
        </DialogHeader>
        
        {/* Stats Header */}
        {report && (
          <div className="flex-shrink-0 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-700">{report.hive_count}</div>
                <div className="text-xs text-purple-600">Total Hives</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-700">{report.active_hives}</div>
                <div className="text-xs text-green-600">Active</div>
              </div>
              {report.ready_hives > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-sky-700">{report.ready_hives}</div>
                  <div className="text-xs text-sky-600">Ready</div>
                </div>
              )}
              {report.inactive_hives > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-700">{report.inactive_hives}</div>
                  <div className="text-xs text-amber-600">Inactive</div>
                </div>
              )}
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-700">{report.total_inspections}</div>
                <div className="text-xs text-blue-600">Inspections</div>
              </div>
            </div>
          </div>
        )}
        
        {/* Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <Home className="h-12 w-12 text-purple-400 animate-pulse" />
                <Sparkles className="h-6 w-6 text-amber-500 absolute -top-1 -right-1 animate-bounce" />
              </div>
              <p className="text-stone-600 mt-4 text-center">
                Analyzing your entire apiary...
                <br />
                <span className="text-sm text-stone-400">This comprehensive report may take a moment</span>
              </p>
            </div>
          )}
          
          {error && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-800 mb-2">Error</h3>
              <p className="text-sm text-stone-600 mb-4">{error}</p>
              <Button onClick={fetchReport} className="bg-purple-600 hover:bg-purple-700 text-white">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          )}
          
          {report && !loading && (
            <div className="prose prose-stone prose-sm max-w-none">
              <ReactMarkdown components={markdownComponents}>
                {report.report}
              </ReactMarkdown>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 pt-4 border-t border-stone-200 flex gap-3">
          {report && !loading && (
            <Button
              variant="secondary"
              onClick={fetchReport}
              className="flex-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Regenerate Report
            </Button>
          )}
          <Button
            variant={report ? "default" : "secondary"}
            onClick={handleClose}
            className={report ? "flex-1 bg-purple-600 hover:bg-purple-700 text-white" : "flex-1"}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AllHivesAIReport;
